import axios from "axios";

/**
 * Client HTTP de l'API IBACY.
 *
 * Authentification par session Sanctum (cookie HttpOnly) : aucun jeton n'est stocké côté navigateur.
 * Le cookie XSRF-TOKEN est renvoyé automatiquement dans l'en-tête X-XSRF-TOKEN.
 */
const http = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL,
  withCredentials: true,
  withXSRFToken: true,
  timeout: 60000,
  headers: {
    Accept: "application/json",
    "X-Requested-With": "XMLHttpRequest",
  },
});

const BASE_URL = String(process.env.VUE_APP_BASE_URL || "").replace(/\/?$/, "/");

/**
 * URL absolue d'une ressource de l'API (photo, document…) : les chemins renvoyés par l'API sont relatifs
 * à VUE_APP_BASE_URL, de sorte que toutes les requêtes du front visent la même URL d'API.
 */
export function apiUrl(path) {
  if (!path) return "";
  if (/^(https?:|data:|blob:)/i.test(path)) return path;
  return BASE_URL + String(path).replace(/^\/+/, "");
}

let csrfPromise = null;

/**
 * Initialise (une seule fois à la fois) le cookie CSRF de Sanctum.
 */
export function ensureCsrfCookie(force = false) {
  if (force || !csrfPromise) {
    const origin = new URL(process.env.VUE_APP_BASE_URL).origin;
    csrfPromise = http.get(`${origin}/sanctum/csrf-cookie`).catch((error) => {
      csrfPromise = null;
      throw error;
    });
  }
  return csrfPromise;
}

const listeners = new Set();

/**
 * Permet au routeur / au store de réagir aux erreurs globales (session expirée, accès refusé...).
 */
export function onHttpEvent(callback) {
  listeners.add(callback);
  return () => listeners.delete(callback);
}

const emit = (type, payload) => listeners.forEach((callback) => callback(type, payload));

/**
 * Requêtes refusées pendant le verrouillage (423) : elles patientent et sont rejouées au déverrouillage,
 * de sorte que la page reprend exactement là où elle en était.
 */
let lockedRequests = [];

export function releaseLockedRequests(unlocked) {
  const waiting = lockedRequests;
  lockedRequests = [];
  waiting.forEach((resume) => resume(unlocked));
}

const isMutation = (config) => ["post", "put", "patch", "delete"].includes((config?.method || "").toLowerCase());

http.interceptors.request.use(async (config) => {
  if (isMutation(config) && !String(config.url).includes("sanctum/csrf-cookie")) {
    await ensureCsrfCookie();
  }
  return config;
});

http.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { response, config } = error;

    if (!response) {
      emit("network", error);
      return Promise.reject(error);
    }

    // Jeton CSRF expiré : on le renouvelle et on rejoue une seule fois la requête.
    if (response.status === 419 && config && !config.__retried) {
      config.__retried = true;
      await ensureCsrfCookie(true);
      return http(config);
    }

    if (response.status === 401 && !config?.skipAuthRedirect && !String(config?.url).includes("login")) {
      emit("unauthenticated", response);
    }

    if (response.status === 403) {
      const code = response.data?.code;
      if (code === "PASSWORD_CHANGE_REQUIRED") emit("password-change", response);
      else if (code === "LICENSE_EXPIRED") emit("license-expired", response);
    }

    if (response.status === 423 && response.data?.code === "APP_LOCKED" && config) {
      emit("locked", response);
      return new Promise((resolve, reject) => {
        lockedRequests.push((unlocked) => (unlocked ? resolve(http(config)) : reject(error)));
      });
    }

    if (response.status === 429) {
      emit("throttled", response);
    }

    return Promise.reject(error);
  }
);

/**
 * Message lisible d'une erreur d'API.
 */
export function errorMessage(error, fallback = "Une erreur est survenue. Veuillez réessayer.") {
  if (!error?.response) {
    return "Impossible de joindre le serveur. Vérifiez votre connexion.";
  }
  const data = error.response.data || {};
  if (error.response.status === 422 && data.errors) {
    const first = Object.values(data.errors)[0];
    return Array.isArray(first) ? first[0] : data.message || fallback;
  }
  return data.message || fallback;
}

/**
 * Erreurs de validation (422) indexées par champ : { champ: "message" }.
 */
export function validationErrors(error) {
  const errors = error?.response?.status === 422 ? error.response.data?.errors || {} : {};
  return Object.fromEntries(Object.entries(errors).map(([key, messages]) => [key, Array.isArray(messages) ? messages[0] : messages]));
}

export default http;
